import { Injectable, inject } from '@angular/core';
import * as Highcharts from 'highcharts/highstock';
import { cloneDeep, merge } from 'lodash';
import { colorMapping } from '../ag1-chart-color-palette';
import {
  CustomChartOptions,
  HighChartsCustomConfig,
  CustomLegend,
  ReportConfig,
} from '@app/core/interfaces/report.interface';
import { FormatService } from './format.service';

// For legacy BB reports
interface IReportData {
  Dimension: string;
  Measure: number;
}

@Injectable()
export class Ag1PieChartService {
  private formatService = inject(FormatService);

  chartOptions: Highcharts.Options = {};

  constructor() {}

  getOptions(
    reportConfig: ReportConfig,
    reportData: IReportData[],
    defaultGeneralOptions: Highcharts.Options,
  ): Highcharts.Options {
    const defaultPieChartOptions = this.getDefaultPieChartOptions();
    const pieChartOptions = this.combineChartOptions(
      reportConfig,
      defaultGeneralOptions,
      defaultPieChartOptions,
    );
    const highChartsCustomConfig: HighChartsCustomConfig =
      reportConfig?.highChartsCustom as HighChartsCustomConfig;

    // Generate series data
    const pieSeries: Highcharts.PointOptionsObject[] = this.generatePieSeries(
      reportData,
      highChartsCustomConfig,
    );
    (pieChartOptions.series[0] as Highcharts.SeriesPieOptions).data = pieSeries;

    // Apply custom point format if specified
    this.applyPointFormat(pieChartOptions, highChartsCustomConfig?.pointFormat);

    this.chartOptions = pieChartOptions;
    return this.chartOptions;
  }

  private getDefaultPieChartOptions(): Highcharts.Options {
    return {
      chart: this.getPieChartTypeAndEvents(),
      legend: this.getLegendOptions(),
      plotOptions: this.getPlotOptions(),
      series: this.getSeriesOptions(),
    };
  }
  /////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////
  // #region         getOptions helpers           //
  //////////////////////////////////////////////////
  private combineChartOptions(
    reportConfig: ReportConfig,
    defaultGeneralOptions: Highcharts.Options,
    defaultPieChartOptions: Highcharts.Options,
  ): Highcharts.Options {
    const configChartOptions = cloneDeep(reportConfig?.chartOptions);

    // Merge default, pie chart, and config-specific options
    const pieChartOptions = merge(
      {},
      defaultGeneralOptions,
      defaultPieChartOptions,
      configChartOptions,
    );

    return pieChartOptions;
  }

  private generatePieSeries(
    reportData: IReportData[],
    highChartsCustomConfig: HighChartsCustomConfig,
  ): Highcharts.PointOptionsObject[] {
    return reportData.map((row: IReportData) => {
      // pie chart fields are determined by custom properties "pieDimension" and "pieMeasure
      if (
        highChartsCustomConfig?.pieMeasure &&
        highChartsCustomConfig?.pieDimension
      ) {
        const seriesItem: Highcharts.PointOptionsObject = {
          name: row[highChartsCustomConfig?.pieDimension] || 'undefined', // explicitly use string 'undefined' for null/missing categories
          y: row[highChartsCustomConfig.pieMeasure],
          color:
            colorMapping[row[highChartsCustomConfig.pieDimension]] || undefined, // Map colors if available
        };
        return seriesItem;
      }
      // if custom properties "pieDimension" and "pieMeasure" are not provided then the data preparation
      // should have fields named "Dimension" and "Measure" (to be backwards compatible with old pie chart configs)
      const seriesItem: Highcharts.PointOptionsObject = {
        name: row.Dimension || 'undefined',
        y: row.Measure,
        color: colorMapping[row.Dimension] || undefined, // Map colors if available
      };
      return seriesItem;
    });
  }

  private applyPointFormat(
    pieChartOptions: Highcharts.Options,
    pointFormat: string | undefined,
  ): void {
    if (pointFormat === 'point.y') {
      pieChartOptions.plotOptions.series.dataLabels[0] = merge(
        {},
        pieChartOptions.plotOptions.series.dataLabels[0],
        {
          format: null,
          formatter: ((component: Ag1PieChartService) =>
            function (this: Highcharts.Point) {
              return component.formatService.formatNumberCompact(this.y);
            })(this),
        },
      );
    }
  }
  /////////////////////////////////////////////////////////////////

  /////////////////////////////////////////////////////////////
  // #region    getDefaultPieChartOptions helpers            //
  /////////////////////////////////////////////////////////////
  private getPieChartTypeAndEvents(): CustomChartOptions {
    return {
      type: 'pie',
      events: {
        render: ((component: Ag1PieChartService) =>
          function (this: Highcharts.Chart) {
            component.handlePieChartRender.call(this, component.formatService);
          })(this),
        resize: ((component: Ag1PieChartService) =>
          function (this: Highcharts.Chart) {
            component.handlePieChartResize.call(this, component.formatService);
          })(this),
      },
    } as CustomChartOptions;
  }

  private handlePieChartRender(
    this: Highcharts.Chart,
    service: FormatService,
  ): void {
    const chart = this as Highcharts.Chart & {
      options: CustomChartOptions;
    };

    const total = chart.series[0].data.reduce((acc, point) => acc + point.y, 0);

    // Accessing the first series assuming it's the pie or donut series
    const series = chart.series[0].options as Highcharts.SeriesPieOptions;

    let x = chart.plotWidth / 2 + chart.plotLeft;
    let y = chart.plotHeight / 2 + chart.plotTop;

    // Convert percentage center to pixel coordinates if specified
    if (series.center && series.center.length >= 2) {
      const centerX = series.center[0];
      const centerY = series.center[1];

      if (typeof centerX === 'string' && centerX.endsWith('%')) {
        x = (parseFloat(centerX) / 100) * chart.plotWidth + chart.plotLeft;
      } else if (typeof centerX === 'number') {
        x = centerX + chart.plotLeft;
      }

      if (typeof centerY === 'string' && centerY.endsWith('%')) {
        y = (parseFloat(centerY) / 100) * chart.plotHeight + chart.plotTop;
      } else if (typeof centerY === 'number') {
        y = centerY + chart.plotTop;
      }
    }

    const labelValue = service.formatNumberCompact(total);

    if (!chart.options.customLabel) {
      chart.options.customLabel = chart.renderer
        .text(labelValue, x, y)
        .css({
          color: '#334155',
          fontSize: '200%',
          fontFamily: 'DM Sans',
          fontWeight: '500',
        })
        .attr({
          align: 'center',
          zIndex: 10,
        })
        .add();
    } else {
      chart.options.customLabel.attr({
        text: labelValue,
        zIndex: 10,
      });
    }

    // Cast legend to the custom type to access legendWidth
    const legend = chart.legend as CustomLegend;
    if (legend && legend.title) {
      legend.title.attr('x', legend.legendWidth / 2);
      legend.title.attr('text-anchor', 'middle');
    }
  }

  private handlePieChartResize(
    this: Highcharts.Chart,
    service: FormatService,
  ): void {
    const chart = this as Highcharts.Chart & {
      options: CustomChartOptions;
    };

    // Recalculate label position on container resize
    const total = chart.series[0].data.reduce((acc, point) => acc + point.y, 0);

    // Update x and y coordinates based on updated chart dimensions
    const x = chart.plotWidth / 2 + chart.plotLeft;
    const y = chart.plotHeight / 2 + chart.plotTop;

    const labelValue = service.formatNumberCompact(total);

    if (chart.options.customLabel) {
      chart.options.customLabel.css({ fontSize: '200%' }).attr({
        text: labelValue,
        x,
        y,
        zIndex: 10,
      });
    }
  }

  private getLegendOptions(): Highcharts.LegendOptions {
    return {
      align: 'center',
      title: {
        style: {
          color: '#4B5563',
        },
      },
    };
  }

  private getPlotOptions(): Highcharts.PlotOptions {
    return {
      series: {
        allowPointSelect: true,
        cursor: 'pointer',
        borderRadius: 6,
        dataLabels: [
          {
            enabled: true,
            distance: '5%',
            format: '{point.percentage:.0f}%',
            style: {
              fontSize: '100%',
              fontFamily: 'DM Sans',
              fontWeight: 'normal',
            },
            filter: {
              operator: '>',
              property: 'percentage',
              value: 5,
            },
            connectorWidth: 0,
          },
        ],
        showInLegend: true,
      } as Highcharts.SeriesPieDataLabelsOptionsObject,
    };
  }

  private getSeriesOptions(): Highcharts.SeriesPieOptions[] {
    return [
      {
        type: 'pie',
        name: 'Total',
        colorByPoint: true,
        innerSize: '55%',
        center: ['50%', '50%'],
      } as Highcharts.SeriesPieOptions,
    ];
  }
}
